import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Category, GiftCardV2 } from '~models';
import { environment } from '../../../../environments/environment';
import { constants } from '../../../app.constants';
import { EStaticCategory } from 'src/app/shared-data/giftcard-category.service';
import Fuse from 'fuse.js';
import { GiftcardCategoryV2Service } from 'src/app/shared-data';
import { FeatureFlagService } from '~services/feature-flag/feature-flag.service';
import { GiftCardDetails, GiftCardPreview } from '~models/shop-gift-card.model';
import { ShopCentralService } from '~services/v2/shop-central/shop-central.service';

@Injectable()
export class GiftCardsV2Service {
  private giftCards: GiftCardV2[] = [];
  private staticCategory = EStaticCategory;

  constructor(
    private ngHttp: HttpClient,
    public giftcardCategoryV2Service: GiftcardCategoryV2Service,
    private readonly featureFlagService: FeatureFlagService,
    private shopCentralService: ShopCentralService,
  ) {}

  async getCards(): Promise<GiftCardV2[]> {
    if (this.giftCards.length > 0) {
      return this.giftCards;
    }

    if (this.featureFlagService.isShopCentralEnabled) {
      const shopGiftCards = (
        await this.ngHttp
          .get<any>(`${environment.shopCentralUri}/giftcards`, {
            params: {
              skip: '0',
              take: '500',
            },
          })
          .toPromise()
      ).result as GiftCardPreview[];

      this.giftCards = shopGiftCards.map((card) =>
        this.shopCentralService.convertGiftCardPreviewToGiftCardV2(card),
      ) as GiftCardV2[];
      return this.giftCards;
    }

    const params = new URLSearchParams();
    params.set('hideDisabled', 'true');

    const url = `${environment.v2ApiUri}/products?${params.toString()}`;

    const response = await this.ngHttp.get<GiftCardV2[]>(url).toPromise();
    this.giftCards = response;

    return this.giftCards;
  }

  async getShopFeaturedCards(): Promise<GiftCardV2[]> {
    try {
      const shopUrl = `${environment.shopCentralUri}/giftcards/feed`;
      const shopFeaturedCards = (await this.ngHttp.get<any>(shopUrl).toPromise())
        .featured as GiftCardPreview[];
      return shopFeaturedCards.map((card) =>
        this.shopCentralService.convertGiftCardPreviewToGiftCardV2(card),
      );
    } catch (error) {
      return [];
    }
  }

  async getFeaturedCards(): Promise<string[]> {
    try {
      const url = `${environment.v2ApiUri}/products/featured/ids?hideDisabled=true`;
      const featuredCardsResult = await this.ngHttp.get<string[]>(url).toPromise();

      return featuredCardsResult || constants.featured;
    } catch (error) {
      return constants.featured;
    }
  }

  async search(query: string): Promise<GiftCardV2[]> {
    if (!query.length) {
      return undefined;
    }

    if (!this.featureFlagService.isShopCentralEnabled) {
      return this.getCardsByKeyword(query);
    }

    if (this.featureFlagService.isShopCentralEnabled && query.length >= 2) {
      try {
        const url = `${environment.shopCentralUri}/giftcards/search?query=${query}`;
        const cards = (await this.ngHttp.get<any>(url).toPromise()).result as GiftCardPreview[];
        return cards.map((card) =>
          this.shopCentralService.convertGiftCardPreviewToGiftCardV2(card),
        );
      } catch (error) {
        throw error;
      }
    }
  }

  getCardsByKeyword(query: string): GiftCardV2[] {
    if (!query.length) {
      return undefined;
    }

    // First, we perform a fuzzy search based on brand and keywords properties
    const fuzzySearchOptions: Fuse.IFuseOptions<GiftCardV2> = {
      threshold: 0.4,
      ignoreLocation: true,
      keys: ['brand', 'keywords'],
    };

    const fuzzySearchFuse = new Fuse(this.giftCards, fuzzySearchOptions);

    const fuzzySearchResult = fuzzySearchFuse.search(query);

    // Second, as the importantContent is a huge string, fuzzy search on this produces a very fuzzy result
    // Therefore, we perform an exact search on this property.
    const exactSearchOptions: Fuse.IFuseOptions<GiftCardV2> = {
      threshold: 0.1,
      ignoreLocation: true,
      keys: ['importantContent'],
    };

    const exactSearchFuse = new Fuse(
      this.giftCards.filter((gc) => !fuzzySearchResult.map((f) => f.item.id).includes(gc.id)),
      exactSearchOptions,
    );

    const exactSearchResult = exactSearchFuse.search(query);

    // Finally, combine the results from the 2 sets of search.
    return [...fuzzySearchResult, ...exactSearchResult].map((sr) => sr.item);
  }

  private async getCardFromGiftCardApi(id: String) {
    try {
      const params = new URLSearchParams();
      params.set('hideDisabled', 'true');

      const giftCard = await this.ngHttp
        .get<GiftCardV2>(`${environment.v2ApiUri}/products/${id}?${params.toString()}`)
        .toPromise();
      if (!giftCard) {
        const error = new Error(`Card not found with the id: "${id}"`);
        throw error;
      }

      return giftCard;
    } catch (error) {
      throw error;
    }
  }

  private async getCardFromShop(id: String) {
    try {
      const url = `${environment.shopCentralUri}/giftcards/${id}`;
      const shopGiftCard = await this.ngHttp.get<GiftCardDetails>(url).toPromise();
      return this.shopCentralService.convertGiftCardDetailsToGiftCardV2(shopGiftCard);
    } catch (error) {
      if (error instanceof HttpErrorResponse) {
        if (error.status === 404) {
          return await this.getCardFromGiftCardApi(id);
        }
      }
      throw error;
    }
  }

  async getCard(id: String) {
    if (this.featureFlagService.isShopCentralEnabled) {
      return await this.getCardFromShop(id);
    }
    return await this.getCardFromGiftCardApi(id);
  }

  async getCardCategories(): Promise<Category[]> {
    let url = `${environment.v2ApiUri}/categories`;
    if (this.featureFlagService.isShopCentralEnabled) {
      url = `${environment.shopCentralUri}/giftcards/categories`;
    }
    try {
      const allCategories = await this.ngHttp.get<Category[]>(url).toPromise();
      this.giftcardCategoryV2Service.setDataGiftcardCategories(allCategories);
      return allCategories;
    } catch (error) {
      return error;
    }
  }

  async getCategoriesByEntryId(entryId: String) {
    try {
      const url = `${environment.shopCentralUri}/giftcards/${entryId}`;
      const shopGiftCard = await this.ngHttp.get<GiftCardDetails>(url).toPromise();
      if (!shopGiftCard.categories || shopGiftCard.categories.length === 0) return [];
      return shopGiftCard.categories;
    } catch (error) {
      throw error;
    }
  }

  async getCardCategoryById(categoryId: string): Promise<any> {
    if ((Object.values(this.staticCategory) as string[]).includes(categoryId)) {
      return;
    }

    this.giftcardCategoryV2Service.setStateLoadingGiftcardCategory(true);

    try {
      if (this.featureFlagService.isShopCentralEnabled) {
        const shopUrl = `${environment.shopCentralUri}/giftcards`;
        const shopGiftCards = (
          await this.ngHttp
            .get<any>(shopUrl, {
              params: {
                categoryId: categoryId,
                skip: '0',
                take: '150',
              },
            })
            .toPromise()
        ).result as GiftCardPreview[];

        const giftCardsV2 = shopGiftCards.map((card) =>
          this.shopCentralService.convertGiftCardPreviewToGiftCardV2(card),
        );

        this.giftcardCategoryV2Service.setDataGiftcardCategoriesFiltering(giftCardsV2);
        return;
      }

      const url = `${environment.v2ApiUri}/categories/${categoryId}`;

      const category = await this.ngHttp.get<any>(url).toPromise();

      const cardsFilterCategory = this.giftCards.filter(
        (card) =>
          category.productIds.find((cardCategory) => cardCategory === card.id) !== undefined,
      );

      this.giftcardCategoryV2Service.setDataGiftcardCategoriesFiltering(cardsFilterCategory);
      return category;
    } catch (error) {
      return error;
    } finally {
      this.giftcardCategoryV2Service.setStateLoadingGiftcardCategory(false);
    }
  }
}
